import { useEffect, useState } from 'react';

/**
 * The strip along the foot of a candidate's screen.
 *
 * The real test centre screen carries the time of day, the machine's battery
 * and network, the sound, and the way out — and a candidate glances at it
 * exactly as they would at a desk clock. It is reproduced because a screen
 * that looks like the one they will sit is the whole point of the platform.
 *
 * Everything here is real: the clock is the wall clock, the battery is this
 * machine's battery where the browser will say, and the network indicator
 * follows the connection. Painting a full battery on a laptop about to die
 * would be worse than showing nothing at all.
 */

interface Battery {
  level: number;
  charging: boolean;
}

function useWallClock(): string {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    // Ticked on the minute rather than the second: nothing here shows seconds,
    // and a timer waking every second for a clock that changes every sixty is
    // sixty times the work for the same screen.
    const id = window.setInterval(() => setNow(new Date()), 5_000);
    return () => window.clearInterval(id);
  }, []);
  return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
}

function useBattery(): Battery | null {
  const [battery, setBattery] = useState<Battery | null>(null);
  useEffect(() => {
    const supported = navigator as Navigator & {
      getBattery?: () => Promise<BatteryManager>;
    };
    if (!supported.getBattery) return;

    let live: BatteryManager | null = null;
    const read = () => live && setBattery({ level: live.level, charging: live.charging });
    void supported.getBattery().then((b) => {
      live = b;
      read();
      b.addEventListener('levelchange', read);
      b.addEventListener('chargingchange', read);
    });
    return () => {
      live?.removeEventListener('levelchange', read);
      live?.removeEventListener('chargingchange', read);
    };
  }, []);
  return battery;
}

interface BatteryManager extends EventTarget {
  level: number;
  charging: boolean;
}

function useOnline(): boolean {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);
  return online;
}

export function WifiIcon({ on }: { on: boolean }) {
  return (
    <svg viewBox="0 0 20 16" width="18" height="15" aria-hidden="true" focusable="false">
      <g fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" opacity={on ? 1 : 0.35}>
        <path d="M1.5 5.2a13 13 0 0 1 17 0" />
        <path d="M4.5 8.4a8.6 8.6 0 0 1 11 0" />
        <path d="M7.4 11.5a4.3 4.3 0 0 1 5.2 0" />
      </g>
      <circle cx="10" cy="14" r="1.3" fill="currentColor" opacity={on ? 1 : 0.35} />
    </svg>
  );
}

function BatteryIcon({ battery }: { battery: Battery }) {
  const width = Math.max(1, Math.round(battery.level * 14));
  return (
    <svg viewBox="0 0 24 12" width="24" height="12" aria-hidden="true" focusable="false">
      <rect x="0.6" y="0.6" width="18.8" height="10.8" rx="2" fill="none" stroke="currentColor" strokeWidth="1.2" />
      <rect x="21" y="4" width="2.4" height="4" rx="1" fill="currentColor" />
      <rect x="2.5" y="2.5" width={width} height="7" rx="1" fill="currentColor" />
      {battery.charging && (
        <path d="M11 1.6 7.4 6.6h2.6L8.9 10.4l3.9-5.2h-2.7z" fill="var(--paper)" stroke="currentColor" strokeWidth="0.7" />
      )}
    </svg>
  );
}

function SoundIcon() {
  return (
    <svg viewBox="0 0 20 16" width="18" height="15" aria-hidden="true" focusable="false">
      <path d="M2 6h3l4-3.4v10.8L5 10H2z" fill="currentColor" />
      <g fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
        <path d="M12.4 5.4a4 4 0 0 1 0 5.2" />
        <path d="M14.8 3.4a7 7 0 0 1 0 9.2" />
      </g>
    </svg>
  );
}

export function CandidateStatusBar({
  centre,
  onExit,
}: {
  /** The centre's name, or null where the account is not a centre. */
  centre: string | null;
  onExit: () => void;
}) {
  const clock = useWallClock();
  const battery = useBattery();
  const online = useOnline();

  return (
    <footer className="candidate__status">
      {centre && <span className="candidate__platform">{centre}</span>}

      <div className="candidate__gauges">
        <span className="candidate__clock">{clock}</span>
        {battery && (
          <span
            className="candidate__gauge"
            title={`Battery ${Math.round(battery.level * 100)}%${battery.charging ? ', charging' : ''}`}
          >
            <BatteryIcon battery={battery} />
          </span>
        )}
        <span className="candidate__gauge" title={online ? 'Connected' : 'No connection'}>
          <WifiIcon on={online} />
        </span>
        <span className="candidate__gauge" title="Sound">
          <SoundIcon />
        </span>
        <button type="button" className="candidate__exit" onClick={onExit}>
          Exit
        </button>
      </div>
    </footer>
  );
}
